import React, { useState } from "react";
import { Star, Users, Home, Heart, Bath, ShowerHead, Wifi, Car, UtensilsCrossed, BookOpen, MapPin, Mountain, Trees, Zap, Sparkles, Maximize2 } from "lucide-react";
import { useI18n } from "../i18n";
import { Lightbox } from "./Lightbox";
import { imgThumb } from "../lib/imgProxy";

const iconMap = {
  bath: Bath, shower: ShowerHead, balcony: Trees, library: BookOpen,
  kitchen: UtensilsCrossed, wifi: Wifi, raclette: UtensilsCrossed, fondue: UtensilsCrossed,
  parking: Car, central: MapPin, view: Mountain, ev_parking: Zap,
};

export const PropertyCard = ({ property: p }) => {
  const { t, lang } = useI18n();
  const [lightbox, setLightbox] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const cover = p.photos[0];
  const openLightbox = (e) => { e.preventDefault(); e.stopPropagation(); setLightbox(true); };

  return (
    <article className="card-prop rounded-xl group" data-testid={`property-card-${p.id}`}>
      <div
        className="relative aspect-[4/3] overflow-hidden bg-[hsl(var(--bg-warm))] cursor-zoom-in"
        onClick={openLightbox}
        data-testid={`property-photo-${p.id}`}
      >
        {!loaded && !errored && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-[hsl(var(--beige-deep))]/40 border-t-[hsl(var(--gold))] animate-spin" />
          </div>
        )}

        <img
          src={imgThumb(cover)}
          alt={p.name}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => { setErrored(true); setLoaded(true); }}
        />

        {errored && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-[hsl(var(--muted-ink))]">
            {p.name}
          </div>
        )}

        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow" aria-hidden="true">
          <Maximize2 className="w-4 h-4 text-[hsl(var(--ink))]" />
        </div>

        {p.favorite && (
          <div className="absolute top-3 left-3 favorite-badge text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <Heart className="w-3 h-3 fill-current" /> {t.properties.favorite}
          </div>
        )}

        <div className="absolute bottom-3 right-3 rating-badge flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs">
          {p.rating ? (
            <>
              <Star className="w-3.5 h-3.5 fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" />
              <span>{p.rating.toFixed(2)}</span>
              <span className="opacity-70">·</span>
              <span className="opacity-80">{p.reviews} {t.properties.reviews}</span>
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5 fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" />
              <span className="uppercase tracking-wider text-[10px] font-semibold">{t.properties.newBadge}</span>
            </>
          )}
        </div>

        {p.photos.length > 1 && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/40 text-white text-[11px] px-2.5 py-1 rounded-full backdrop-blur">
            <span>{p.photos.length}</span>
            <span className="opacity-80">photos</span>
          </div>
        )}
      </div>

      <div className="p-6">
        <h3 className="font-serif text-xl leading-tight mb-2">{p.name}</h3>
        <div className="flex items-center gap-4 text-xs text-[hsl(var(--muted-ink))] mb-4">
          <span className="flex items-center gap-1.5"><Home className="w-3.5 h-3.5" />{p.type[lang]}</span>
          <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{lang === 'fr' ? p.capacity : p.capacityEn} {t.properties.people}</span>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-5">
          {p.amenities.slice(0, 6).map((a) => {
            const Icon = iconMap[a] || Home;
            return (
              <span key={a} className="inline-flex items-center gap-1 text-[11px] bg-[hsl(var(--bg-warm))] px-2.5 py-1 rounded-full text-[hsl(var(--ink-soft))]">
                <Icon className="w-3 h-3" /> {t.amen[a] || a}
              </span>
            );
          })}
          {p.amenities.length > 6 && (
            <span className="inline-flex items-center text-[11px] px-2.5 py-1 rounded-full text-[hsl(var(--muted-ink))]">+{p.amenities.length - 6}</span>
          )}
        </div>

        {p.airbnb ? (
          <a href={p.airbnb} target="_blank" rel="noopener noreferrer" data-testid={`airbnb-btn-${p.id}`}
            className="block w-full text-center btn-gold px-5 py-3 rounded-full text-sm font-semibold">
            {t.properties.book} →
          </a>
        ) : (
          <button disabled
            className="block w-full text-center px-5 py-3 rounded-full text-sm font-semibold bg-[hsl(var(--bg-warm))] text-[hsl(var(--muted-ink))] cursor-not-allowed border border-[hsl(var(--line))]"
            data-testid={`airbnb-btn-${p.id}`}>
            {t.properties.bookSoon}
          </button>
        )}
      </div>
      {lightbox && (
        <Lightbox photos={p.photos} startIndex={0} propertyName={p.name} onClose={() => setLightbox(false)} />
      )}
    </article>
  );
};
